import React, { useState } from "react";

import {
  Text,
  Card,
  RangeSlider,
  Stack,
  Group,
  Button,
  Badge,
  NumberInput,
  NumberInputProps,
  useMantineTheme,
} from "@mantine/core";
import { useFocusWithin } from "@mantine/hooks";

import { useSearchRouteInfo } from "hooks/routing";

import { FilterDropdownButton } from "./FilterDropdownButton";
import { MobileAwareDropdown } from "./MobileAwareDropdown";
import { Eyebrow } from "../mantine-extended/eyebrow";

const MIN_VALUE = 0;
const MAX_VALUE = 999_000_000;
const STEP = 1_000_000;

interface CapRaisedDropdownProps {
  handleChange: (key: string, value: [number, number] | null) => void;
  queryParamName: string;
}

export function CapRaisedDropdown(props: CapRaisedDropdownProps): JSX.Element {
  const { query } = useSearchRouteInfo();
  const { ref, focused } = useFocusWithin();

  function getRangesFromQuery(): [number, number] {
    const minValue = query.capitalRaised?.[0] ?? MIN_VALUE;
    const maxValueFromQuery = query.capitalRaised?.[1];
    const maxValue = maxValueFromQuery === Infinity ? MAX_VALUE : maxValueFromQuery ?? MAX_VALUE;

    return [minValue, maxValue];
  }

  const [range, setRange] = useState<[number, number]>(getRangesFromQuery());

  function renderButton(onClick?: () => void) {
    return (
      <FilterDropdownButton activeBackground={!isNoFilter(range)} onClick={onClick}>
        <Text size="sm">Capital Raised</Text>
        {!isNoFilter(range) && (
          <Badge color="brand">{`${formatNumber(range[0])} - ${formatNumber(range[1])}`}</Badge>
        )}
      </FilterDropdownButton>
    );
  }

  function renderCard() {
    const sharedInputProps: NumberInputProps = {
      min: MIN_VALUE,
      max: MAX_VALUE / STEP,
      hideControls: true,
      rightSection: <Text>M</Text>,
      onBlur: () => props.handleChange(props.queryParamName, stripMax(range)),
      onKeyDown: (e) => {
        if (e.key === "Enter") {
          props.handleChange(props.queryParamName, stripMax(range));
        }
      },
    };

    return (
      <Card
        shadow="md"
        sx={{
          overflow: "visible",
        }}
      >
        <Card.Section p={16} pt={24}>
          <Stack gap={24}>
            <CapRaisedSlider
              handleChangeEnd={props.handleChange}
              queryParamName={props.queryParamName}
              range={range}
              onChange={(v) => {
                setRange(v);
              }}
            />
            <Stack>
              <Group
                justify="center"
                sx={{
                  fontWeight: 500,
                }}
              >
                <NumberInput
                  value={range[0] / STEP}
                  onChange={(v) => {
                    if (typeof v === "number") {
                      const newVal = Math.min(v * STEP, MAX_VALUE);
                      setRange([newVal, range[1]]);
                    }
                  }}
                  prefix="$"
                  {...sharedInputProps}
                />

                <Text>-</Text>
                <div ref={ref}>
                  <NumberInput
                    value={range[1] / STEP}
                    prefix="$"
                    onChange={(v) => {
                      if (typeof v === "number") {
                        const newVal = Math.min(v * STEP, MAX_VALUE);
                        setRange([range[0], newVal]);
                      }
                    }}
                    {...sharedInputProps}
                  />
                </div>
              </Group>
              <Group justify="space-between" px={6}>
                <Eyebrow>MIN</Eyebrow>
                <Eyebrow>MAX</Eyebrow>
              </Group>
            </Stack>
          </Stack>
        </Card.Section>
        <Card.Section withBorder p={5}>
          <Group justify="flex-start">
            <Button
              variant="secondary"
              h={30}
              onClick={() => {
                setRange([MIN_VALUE, MAX_VALUE]);
                props.handleChange(props.queryParamName, null);
              }}
            >
              Clear
            </Button>
          </Group>
        </Card.Section>
      </Card>
    );
  }

  return <MobileAwareDropdown renderButton={renderButton} renderContent={renderCard} />;
}

interface CapRaisedSliderProps {
  handleChangeEnd: (key: string, value: [number, number] | null) => void;
  range: [number, number];
  onChange: (value: [number, number]) => void;
  queryParamName: string;
}

export function CapRaisedSlider(props: CapRaisedSliderProps): JSX.Element {
  const theme = useMantineTheme();

  return (
    <RangeSlider
      defaultValue={[MIN_VALUE, MAX_VALUE]}
      step={STEP}
      min={MIN_VALUE}
      max={MAX_VALUE}
      value={props.range}
      labelTransitionProps={{
        transition: "skew-down",
        duration: 150,
        timingFunction: "ease",
      }}
      label={formatNumber}
      onChange={props.onChange}
      styles={{
        track: {
          "::before": {
            backgroundColor: theme.colors.gray[3],
          },
        },
      }}
      onChangeEnd={(v) => {
        if (isNoFilter(v)) {
          props.handleChangeEnd(props.queryParamName, null);
        } else {
          props.handleChangeEnd(props.queryParamName, stripMax(v));
        }
      }}
    />
  );
}

function formatNumber(value: number): string {
  const formatted = `$${(value / STEP).toLocaleString("en-US")}M`;
  if (value === MAX_VALUE) {
    return `${formatted}+`;
  } else {
    return formatted;
  }
}

function stripMax(value: [number, number]): [number, number] {
  if (value[1] === MAX_VALUE) {
    return [value[0], Infinity];
  }
  return value;
}

function isNoFilter(value: [number, number] | null): boolean {
  if (value === null) {
    return true;
  }
  return value[0] === MIN_VALUE && value[1] === MAX_VALUE;
}
